import React, { useState } from "react";
import { FileText, Search, Lock, Download } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { PageCta } from "@/components/shared/PageCta";
import { Section } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";

export const Resources = () => {
  const { t } = useI18n();
  const p = t("pages.resources");
  const [q, setQ] = useState("");
  useSEO({ title: p.title, description: p.lead, path: "/resources" });

  const cats = (p.categories || []).filter(
    (c) => c.title.toLowerCase().includes(q.toLowerCase()) || c.body.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="resources-page">
        <div className="relative max-w-xl">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/35" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="resource-search"
            aria-label={t("common.searchPlaceholder")}
            placeholder={t("common.searchPlaceholder")}
            className="w-full border border-navy/15 bg-white py-3.5 pl-11 pr-4 text-sm text-navy outline-none transition-colors duration-200 focus:border-grn"
          />
        </div>

        <Stagger className="mt-12 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3" step={0.04}>
          {cats.map((c, i) => (
            <StaggerItem key={c.key} className="bg-white">
              <div className="group h-full p-8" data-testid={`resource-category-${c.key}`}>
                <div className="flex items-start justify-between">
                  <FileText className="h-6 w-6 text-navy/30 transition-colors duration-400 group-hover:text-grn" strokeWidth={1.4} />
                  <span className="font-mono text-[11px] tracking-[0.2em] text-navy/20">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-8 font-display text-lg font-semibold text-navy">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.body}</p>
                <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.2em] text-navy/35">
                  Documents pending publication
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal>
          <p className="mt-10 max-w-3xl border-l-2 border-gold pl-5 text-[12px] leading-relaxed text-slate-500">{p.note}</p>
        </Reveal>
      </Section>
      <PageCta />
    </>
  );
};

export const Downloads = () => {
  const { t } = useI18n();
  const p = t("pages.downloads");
  useSEO({ title: p.title, description: p.lead, path: "/downloads" });

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="downloads-page">
        <Reveal>
          <h2 className="font-display text-2xl font-semibold text-navy sm:text-3xl">Public documents</h2>
        </Reveal>
        <Stagger className="mt-8 divide-y divide-navy/10 border-y border-navy/10">
          {(p.publicItems || []).map((d, i) => (
            <StaggerItem key={d.title}>
              <div className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`download-public-${i + 1}`}>
                <div className="flex items-start gap-4">
                  <Download className="mt-1 h-5 w-5 shrink-0 text-grn" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-base font-semibold text-navy">{d.title}</h3>
                    <p className="mt-1 text-[13px] text-slate-600">{d.body}</p>
                  </div>
                </div>
                <span className="shrink-0 border border-navy/15 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-navy/45">
                  Awaiting upload
                </span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal>
          <h2 className="mt-20 font-display text-2xl font-semibold text-navy sm:text-3xl">Member-restricted documents</h2>
        </Reveal>
        <Stagger className="mt-8 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2">
          {(p.restrictedItems || []).map((d, i) => (
            <StaggerItem key={d} className="bg-slate-50/60">
              <div className="flex items-center gap-4 p-6" data-testid={`download-restricted-${i + 1}`}>
                <Lock className="h-4 w-4 shrink-0 text-navy/40" strokeWidth={1.6} />
                <span className="text-[14px] text-navy/70">{d}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal>
          <p className="mt-10 max-w-3xl border-l-2 border-gold pl-5 text-[12px] leading-relaxed text-slate-500">{p.note}</p>
        </Reveal>
      </Section>
      <PageCta />
    </>
  );
};
